<template>
  <template
    v-if="
      !(toBalance.isFetching.value && toBalance.isFirstFetching.value) ||
      !(fromBalance.isFetching.value && fromBalance.isFirstFetching.value)
    "
  >
    <RegisterToken
      v-if="view === 'register'"
      :token="registeredToken"
      @close="onClose"
      @success="onRegisterSuccess"
      @reject="onRegisterRejected"
    />
    <BurnLiquidityConfirm
      v-if="view === 'burn'"
      @close="onClose"
      @success="onBurnSuccess"
      @reject="onBurnRejected"
    />
  </template>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, watch } from 'vue';
import {
  BurnLiquidityConfirm,
  RegisterToken,
} from '@/components/Dialogs/Steps';
import { useBurnLiquidityStore, useDialogStore, useTokensStore } from '@/store';
import { useCurrentAccountBalance } from '@/composables/useAccountBalance';
import { useI18n } from '@/composables/useI18n';

const dialogRef = inject('dialogRef');

type IProps = {
  visible?: boolean;
};

defineProps<IProps>();
defineEmits(['update:visible']);
const view = ref<'register' | 'burn'>('burn');

const { t } = useI18n();

onBeforeMount(setDialogTitle);

const dialogStore = useDialogStore();
const tokensStore = useTokensStore();
const burnStore = useBurnLiquidityStore();

const toBalance = useCurrentAccountBalance(burnStore.toCurrency?.token);

const fromBalance = useCurrentAccountBalance(burnStore.fromCurrency?.token);

const registeredToken = computed(() => {
  if (
    !toBalance.isExists.value &&
    !(toBalance.isFetching.value && toBalance.isFirstFetching.value)
  ) {
    return burnStore.toCurrency.token;
  }

  if (
    !fromBalance.isExists.value &&
    !(fromBalance.isFetching.value && fromBalance.isFirstFetching.value)
  ) {
    return burnStore.fromCurrency.token;
  }
  return undefined;
});

function onClose() {
  dialogRef.value.close();
}

function onRegisterSuccess() {
  view.value = 'burn';
}

function onRegisterRejected() {
  view.value = 'register';
}

function onBurnSuccess() {
  onClose();
  view.value = 'register';
}

function onBurnRejected() {
  onClose();
  view.value = 'register';
}

function setDialogTitle() {
  const token = tokensStore.getToken(registeredToken.value);
  if (view.value === 'register' && token) {
    dialogStore.setDialogTitle(t('tokens.registerToken', [token.symbol]));
    return;
  }
  dialogStore.setDialogTitle('Remove Liquidity');
}

watch(
  () => [registeredToken.value, view.value],
  async () => {
    view.value = !registeredToken.value ? 'burn' : 'register';

    setDialogTitle();
  },
);
</script>

<style lang="scss" scoped></style>
